import { useMemo } from 'react';

import { useIndirectTaxTransactions } from '@commenda/api/indirect-tax/transactions';

import { IndirectTaxRegion } from '../../common';
import { buildIndirectTaxTransactionApiFilters } from '../../transactions/dashboard/buildTransactionApiFilters';
import { IndirectTaxFilingsHelper } from '../helpers';
import { IIndirectTaxFilingDetail } from '../types';

interface UseIndirectTaxFilingTransactionCountArgs {
  filing: IIndirectTaxFilingDetail;
  region: IndirectTaxRegion;
  corporationId: number;
}

// Live transaction count for a filing, computed from the SAME query the
// "View transactions" modal runs (the filing's reporting-period / jurisdiction /
// status filters → transactions list `totalTransactions`). This keeps the
// Summary metric and the modal in agreement — the stored
// `filing.filingTransactionsCount` is null/0 for VAT filings.
export function useIndirectTaxFilingTransactionCount({
  filing,
  region,
  corporationId,
}: UseIndirectTaxFilingTransactionCountArgs): {
  count: number;
  isLoading: boolean;
} {
  const apiFilters = useMemo(
    () =>
      buildIndirectTaxTransactionApiFilters({
        corporationId,
        region,
        filters: IndirectTaxFilingsHelper.buildTransactionFiltersForFiling(
          filing,
          region
        ),
      }),
    [corporationId, region, filing]
  );

  // limit: 1 — we only need the total count, not the rows. sortState is
  // required by the params type but irrelevant to the count.
  const { data, isLoading } = useIndirectTaxTransactions({
    filters: apiFilters,
    limit: 1,
    sortState: { id: 'date', desc: true },
  });

  return { count: data.totalTransactions, isLoading };
}
